import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Plus, Search, Wrench } from 'lucide-react';

export default function MaintenancePlan() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState({ department: '', job_type: '', trigger_type: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [components, setComponents] = useState([]);

  useEffect(() => { loadJobs(); loadComponents(); }, [filter]);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.department) params.department = filter.department;
      if (filter.job_type) params.job_type = filter.job_type;
      if (filter.trigger_type) params.trigger_type = filter.trigger_type;
      if (search) params.search = search;
      const res = await api.get('/work-orders/maintenance-plan/list', { params });
      setJobs(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadComponents = async () => {
    try {
      const res = await api.get('/inventory/components');
      setComponents(res.data);
    } catch (e) { console.error(e); }
  };

  const filtered = jobs.filter(j => !search || j.job_title?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="action-bar">
        <div className="search-bar" style={{ flex: 1, maxWidth: 400 }}>
          <input className="form-input" placeholder="Search jobs..." value={search} onChange={e => { setSearch(e.target.value); loadJobs(); }} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select className="form-select" value={filter.department} onChange={e => setFilter({ ...filter, department: e.target.value })}>
            <option value="">All Depts</option>
            <option value="Engine">Engine</option>
            <option value="Deck">Deck</option>
            <option value="Electrical">Electrical</option>
          </select>
          <select className="form-select" value={filter.job_type} onChange={e => setFilter({ ...filter, job_type: e.target.value })}>
            <option value="">All Types</option>
            <option value="Preventive">Preventive</option>
            <option value="Inspection">Inspection</option>
            <option value="Overhaul">Overhaul</option>
            <option value="Statutory">Statutory</option>
            <option value="Class">Class</option>
          </select>
          <button className="btn btn-accent" onClick={() => setShowCreate(true)}><Plus size={15} /> Add Job</button>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="card-body">{[...Array(5)].map((_, i) => <div key={i} className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div>)}</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><Wrench /><h4>No jobs in maintenance plan</h4><p>Add your first maintenance job to get started.</p></div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr><th>Job #</th><th>Job Title</th><th>Equipment</th><th>Dept</th><th>Type</th><th>Trigger</th><th>Interval</th><th>Last Done</th><th>Next Due</th><th>Resp.</th><th>Statutory</th><th>Open WOs</th></tr>
              </thead>
              <tbody>
                {filtered.map(j => {
                  const isOverdue = j.next_due_date && new Date(j.next_due_date) < new Date();
                  return (
                    <tr key={j.id} style={{ cursor: 'pointer' }}>
                      <td>{j.job_number}</td>
                      <td><strong>{j.job_title}</strong></td>
                      <td>{j.component_name || '—'}</td>
                      <td>{j.comp_department || j.department}</td>
                      <td><span className="badge badge-draft">{j.job_type}</span></td>
                      <td>{j.trigger_type}</td>
                      <td>
                        {j.interval_days ? `${j.interval_days} days` : j.interval_hours ? `${j.interval_hours} hrs` : '—'}
                        {j.tolerance_days > 0 && <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}> ±{j.tolerance_days}d</span>}
                      </td>
                      <td>{j.last_done_date || 'Never'}</td>
                      <td style={{ color: isOverdue ? 'var(--danger)' : 'var(--text)', fontWeight: isOverdue ? 600 : 400 }}>{j.next_due_date || '—'}</td>
                      <td>{j.responsible_role?.replace(/_/g, ' ')}</td>
                      <td>{j.is_statutory ? <span className="badge badge-high">Yes</span> : '—'}</td>
                      <td>{j.open_wos > 0 ? <span className="badge badge-warning">{j.open_wos}</span> : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && <CreateJobModal components={components} onClose={() => setShowCreate(false)} onCreated={loadJobs} />}
    </div>
  );
}

function CreateJobModal({ components, onClose, onCreated }) {
  const [form, setForm] = useState({
    job_title: '', component_id: '', job_type: 'Preventive', trigger_type: 'Calendar',
    interval_days: '', interval_hours: '', tolerance_days: 0, responsible_role: '',
    procedure_text: '', estimated_hours: '', is_statutory: false
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/work-orders/maintenance-plan', form);
      onCreated();
      onClose();
    } catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>Add Maintenance Job</h2><button className="btn btn-outline btn-sm" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group"><label className="form-label">Job Title *</label>
              <input className="form-input" value={form.job_title} onChange={e => setForm({ ...form, job_title: e.target.value })} required /></div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Equipment</label>
                <select className="form-select" value={form.component_id} onChange={e => setForm({ ...form, component_id: e.target.value })}>
                  <option value="">Select...</option>
                  {components.map(c => <option key={c.id} value={c.id}>{c.component_name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Job Type *</label>
                <select className="form-select" value={form.job_type} onChange={e => setForm({ ...form, job_type: e.target.value })}>
                  {['Preventive','Inspection','Overhaul','Statutory','Class'].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Trigger Type *</label>
                <select className="form-select" value={form.trigger_type} onChange={e => setForm({ ...form, trigger_type: e.target.value })}>
                  {['Calendar','Counter','Both'].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Responsible Role</label>
                <select className="form-select" value={form.responsible_role} onChange={e => setForm({ ...form, responsible_role: e.target.value })}>
                  <option value="">Select...</option>
                  {['chief_engineer','second_engineer','third_engineer','electrical_officer','bosun'].map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Interval (Days)</label>
                <input className="form-input" type="number" value={form.interval_days} onChange={e => setForm({ ...form, interval_days: e.target.value })} placeholder="e.g. 90" /></div>
              <div className="form-group"><label className="form-label">Interval (Hours)</label>
                <input className="form-input" type="number" value={form.interval_hours} onChange={e => setForm({ ...form, interval_hours: e.target.value })} placeholder="e.g. 2000" /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Tolerance (± days)</label>
                <input className="form-input" type="number" value={form.tolerance_days} onChange={e => setForm({ ...form, tolerance_days: parseInt(e.target.value) })} /></div>
              <div className="form-group"><label className="form-label">Estimated Hours</label>
                <input className="form-input" type="number" value={form.estimated_hours} onChange={e => setForm({ ...form, estimated_hours: e.target.value })} /></div>
            </div>
            <div className="form-group"><label className="form-label">Procedure</label>
              <textarea className="form-textarea" value={form.procedure_text} onChange={e => setForm({ ...form, procedure_text: e.target.value })} placeholder="Step-by-step instructions" /></div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_statutory} onChange={e => setForm({ ...form, is_statutory: e.target.checked })} />
                <span style={{ fontSize: 13 }}>Class / Statutory Required</span>
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Creating...' : 'Create Job'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
